/**
 * =============================================================================
 * TURNSTILE WIDGET COMPONENT
 * =============================================================================
 *
 * Cloudflare Turnstile CAPTCHA widget integration.
 * Provides privacy-friendly bot protection for form submissions.
 *
 * ARCHITECTURE REFERENCE: docs/ARCHITECTURE.md §14.1
 * PHASE REFERENCE: PHASE_1_SPEC.md P1-1
 *
 * Features:
 * - Loads Turnstile script dynamically (only when needed)
 * - Handles widget rendering with explicit mode
 * - Manages token lifecycle (success, error, expiration)
 * - Provides loading and error states
 * - Supports widget reset for retry scenarios
 * - Exposes reset via ref for parent components
 *
 * REVIEW FIXES (Session 2):
 * - Added React import for ReactElement return type
 * - Fixed retry button to trigger full re-initialization (not just reset)
 * - Added retryCount state to trigger useEffect on retry
 * - Exposed reset via useImperativeHandle for parent forms
 *
 * =============================================================================
 */

import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface TurnstileWidgetProps {
  /** Turnstile site key from Cloudflare dashboard */
  siteKey: string;
  /** Callback when verification succeeds, provides the token */
  onVerify: (token: string) => void;
  /** Callback when verification fails */
  onError?: (error: string) => void;
  /** Callback when token expires (user should re-verify) */
  onExpire?: () => void;
  /** Callback when widget starts loading */
  onLoad?: () => void;
  /** Whether the widget is disabled */
  disabled?: boolean;
  /** Theme: auto, light, or dark */
  theme?: 'auto' | 'light' | 'dark';
  /** Provider size: fixed normal/compact, or responsive flexible. */
  size?: TurnstileSize;
  /**
   * Rebuild after a provider verification error instead of using the
   * predecessor reset behavior. Slice 2A enables this only for Ask.
   */
  reinitializeOnVerificationError?: boolean;
  /** Additional CSS class name */
  className?: string;
}

export type TurnstileSize = 'normal' | 'compact' | 'flexible';

/**
 * Ref handle for external control of the Turnstile widget.
 */
export interface TurnstileWidgetRef {
  /** Reset the widget to allow re-verification */
  reset: () => void;
}

// Turnstile window types
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: (error: string) => void;
          'expired-callback'?: () => void;
          theme?: 'auto' | 'light' | 'dark';
          size?: TurnstileSize;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

// =============================================================================
// CONSTANTS
// =============================================================================

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

// =============================================================================
// SCRIPT LOADING
// =============================================================================

// Cache loading promise to prevent duplicate script loads
let loadingPromise: Promise<void> | null = null;

/**
 * Load the Turnstile script if not already loaded.
 * Returns a promise that resolves when the script is ready.
 * Uses cached promise to prevent race conditions with multiple widget mounts.
 */
function loadTurnstileScript(): Promise<void> {
  // Return cached promise if loading in progress
  if (loadingPromise) {
    return loadingPromise;
  }

  // Check if already loaded
  if (window.turnstile) {
    return Promise.resolve();
  }

  loadingPromise = new Promise((resolve, reject) => {
    const failLoad = (script: HTMLElement): void => {
      script.remove();
      loadingPromise = null;
      reject(new Error('Failed to load Turnstile'));
    };

    // Check if script element exists but turnstile isn't ready yet
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => failLoad(existingScript), { once: true });
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => failLoad(script);

    document.head.appendChild(script);
  });

  return loadingPromise;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Turnstile CAPTCHA widget for bot protection.
 *
 * @example
 * ```tsx
 * const turnstileRef = useRef<TurnstileWidgetRef>(null);
 * const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
 *
 * // Reset widget when form is reset
 * const handleFormReset = () => {
 *   turnstileRef.current?.reset();
 *   setTurnstileToken(null);
 * };
 *
 * <TurnstileWidget
 *   ref={turnstileRef}
 *   siteKey={config.turnstileSiteKey}
 *   onVerify={(token) => setTurnstileToken(token)}
 *   onError={(err) => console.error('Turnstile error:', err)}
 *   onExpire={() => setTurnstileToken(null)}
 * />
 * ```
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  function TurnstileWidget(
    {
      siteKey,
      onVerify,
      onError,
      onExpire,
      onLoad,
      disabled = false,
      theme = 'auto',
      size = 'normal',
      reinitializeOnVerificationError = false,
      className = '',
    },
    ref
  ): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    // Retry state commits asynchronously, so invalidate the current provider
    // synchronously before requesting a replacement. This closes the interval
    // where an errored instance could auto-recover before effect cleanup and
    // publish a token that outlives the instance being removed.
    const providerGenerationRef = useRef(0);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Distinguish script-load failure (which always needs reinitialization)
    // from a provider verification failure (caller-selected recovery).
    const [isLoadError, setIsLoadError] = useState(false);
    // Increment to trigger re-initialization
    const [retryCount, setRetryCount] = useState(0);

    /**
     * Reset the widget to allow re-verification.
     * If widget exists, resets it. Otherwise triggers full re-initialization.
     */
    const reset = useCallback(() => {
      if (widgetIdRef.current && window.turnstile) {
        // Widget exists, just reset it
        window.turnstile.reset(widgetIdRef.current);
      } else {
        // Widget doesn't exist (load failed), trigger full re-initialization
        providerGenerationRef.current += 1;
        setRetryCount((c) => c + 1);
      }
    }, []);

    /**
     * Handle retry button click. A provider reset alone would leave this
     * component in its error render branch, so every visible retry performs the
     * existing full re-initialization path.
     */
    const handleRetry = useCallback(() => {
      providerGenerationRef.current += 1;
      setIsLoadError(false);
      setRetryCount((c) => c + 1);
    }, []);

    // Expose reset function to parent via ref
    useImperativeHandle(ref, () => ({ reset }), [reset]);

    /**
     * Initialize and render the Turnstile widget.
     */
    useEffect(() => {
      // Skip if disabled or no container
      if (disabled || !containerRef.current) {
        return;
      }

      let mounted = true;
      const providerGeneration = providerGenerationRef.current;
      const isCurrentProvider = (): boolean =>
        mounted && providerGeneration === providerGenerationRef.current;

      const initWidget = async (): Promise<void> => {
        try {
          setStatus('loading');
          setErrorMessage(null);
          setIsLoadError(false);

          // Load the Turnstile script
          await loadTurnstileScript();

          // Check if component is still mounted and we have a container
          if (!isCurrentProvider() || !containerRef.current || !window.turnstile) {
            return;
          }

          // Remove any existing widget in this container
          if (widgetIdRef.current) {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
          }

          // Render the widget
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
              if (isCurrentProvider()) {
                // Cloudflare may recover automatically after an error callback.
                // Keep the local render state aligned with the now-valid token
                // so an enabled submit action never coexists with stale error UI.
                setStatus('ready');
                setErrorMessage(null);
                setIsLoadError(false);
                onVerify(token);
              }
            },
            'error-callback': (error: string) => {
              if (isCurrentProvider()) {
                setStatus('error');
                setErrorMessage(error || 'Verification failed');
                setIsLoadError(false);
                onError?.(error || 'Verification failed');
              }
            },
            'expired-callback': () => {
              if (isCurrentProvider()) {
                onExpire?.();
              }
            },
            theme,
            size,
          });

          if (isCurrentProvider()) {
            setStatus('ready');
            onLoad?.();
          }
        } catch (error) {
          if (isCurrentProvider()) {
            const message = error instanceof Error ? error.message : 'Failed to load verification';
            setStatus('error');
            setErrorMessage(message);
            setIsLoadError(true);
            onError?.(message);
          }
        }
      };

      initWidget();

      // Cleanup on unmount
      return () => {
        mounted = false;
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [siteKey, disabled, theme, size, retryCount, onVerify, onError, onExpire, onLoad]);

    // Don't render anything if disabled
    if (disabled) {
      return <div className={className} />;
    }

    return (
      <div className={`fmp-turnstile ${className}`}>
        {status === 'loading' && (
          <div className="fmp-turnstile__loading" aria-live="polite">
            Loading verification...
          </div>
        )}

        {status === 'error' && (
          <div className="fmp-turnstile__error" role="alert">
            <span>{errorMessage || 'Verification unavailable'}</span>
            <button
              type="button"
              onClick={isLoadError || reinitializeOnVerificationError ? handleRetry : reset}
              className="fmp-turnstile__retry"
              aria-label="Retry verification"
            >
              Retry
            </button>
          </div>
        )}

        <div
          ref={containerRef}
          className="fmp-turnstile__container"
          style={{ display: status === 'ready' ? 'block' : 'none' }}
        />
      </div>
    );
  }
);
